import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import { toFraction, toPercent } from '../percent-scale/percent-scale.util';
import { useScalarAnnouncer } from '../useScalarAnnouncer/useScalarAnnouncer';
import { useScalarNumber, type UseScalarNumber } from '../useScalarNumber/useScalarNumber';

import { type PercentInputProps } from './PercentInput.types';

export type UsePercentInputLogicInput = Omit<
  PercentInputProps,
  'id' | 'className' | 'size' | 'aria-describedby'
>;

export interface UsePercentInputLogic {
  number: UseScalarNumber;
  /** Percent bounds, mapped from the fraction bounds the schema declares. */
  sliderMin: number;
  sliderMax: number;
  sliderStep: number;
  sliderValue: number | undefined;
  sliderLabel: string;
  onSliderChange: (percent: number) => void;
  /** What the slider reads out — "85%", never a bare "85". */
  valueText: string;
  /** What is actually stored, spelled out under the field. */
  storedHint: string;
  emptyHint: string;
  rangeHint: string | undefined;
  announcement: string;
  refuseFromSlider: () => void;
}

const FULL_PERCENT = 100;

/**
 * A 0–1 fraction, edited as the percentage everyone actually means.
 *
 * `verification.faceMatchThreshold` and `moderation.ai.confidenceThreshold` were
 * bare decimal boxes, where `0.9` and `0.09` are one keystroke apart and neither
 * looks wrong. Here the operator types 90, drags a slider through a bounded
 * range, and reads "stored as 0.9" underneath — so the number that reaches the
 * matcher is never a number they had to infer.
 *
 * Precision is preserved rather than imposed: the control steps in whole
 * percent, but a stored 0.855 shows as 85.5 and is written back byte-identical
 * unless it is edited.
 */
export function usePercentInputLogic({
  label,
  value,
  onValueChange,
  min,
  max,
  decimals = 1,
  step = 1,
  isReadOnly = false,
  isDisabled = false,
}: UsePercentInputLogicInput): UsePercentInputLogic {
  const { t } = useTranslation('uiKit');
  const { locale } = useDirection();
  const announcer = useScalarAnnouncer();

  const sliderMin = toPercent(min) ?? 0;
  const sliderMax = toPercent(max) ?? FULL_PERCENT;
  const displayValue = toPercent(value);

  const commit = useCallback(
    (next: number | undefined) => {
      onValueChange(toFraction(next));
    },
    [onValueChange],
  );

  const number = useScalarNumber({
    displayValue,
    decimals,
    step,
    min: sliderMin,
    max: sliderMax,
    isReadOnly,
    isDisabled,
    onCommit: commit,
    onRefuse: announcer.announceReadOnlyRefusal,
  });

  const onSliderChange = useCallback(
    (percent: number) => {
      // The slider shares the field's ONE refusal choke point rather than
      // carrying a second copy of the rule.
      if (isDisabled) return;
      if (isReadOnly) {
        announcer.announceReadOnlyRefusal();
        return;
      }
      number.reset();
      commit(percent);
    },
    [announcer, commit, isDisabled, isReadOnly, number],
  );

  const symbol = t('percent.symbol');
  const shown = number.draftValue ?? displayValue;

  const valueText = `${formatNumber(shown ?? sliderMin, locale)}${symbol}`;

  const emptyHint = t('scalar.empty');
  const storedHint =
    value === null || value === undefined
      ? emptyHint
      : t('percent.stored', { value: formatNumber(value, locale, { maximumFractionDigits: 4 }) });

  const rangeHint =
    min === undefined && max === undefined
      ? undefined
      : t('percent.range', {
          min: `${formatNumber(sliderMin, locale)}${symbol}`,
          max: `${formatNumber(sliderMax, locale)}${symbol}`,
        });

  return useMemo(
    () => ({
      number,
      sliderMin,
      sliderMax,
      sliderStep: step,
      sliderValue: shown,
      sliderLabel: t('percent.sliderFor', { label }),
      onSliderChange,
      valueText,
      storedHint,
      emptyHint,
      rangeHint,
      announcement: announcer.announcement,
      refuseFromSlider: announcer.announceReadOnlyRefusal,
    }),
    [
      announcer.announceReadOnlyRefusal,
      announcer.announcement,
      emptyHint,
      label,
      number,
      onSliderChange,
      rangeHint,
      shown,
      sliderMax,
      sliderMin,
      step,
      storedHint,
      t,
      valueText,
    ],
  );
}
